import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import { CheckCircle, XCircle, Loader2, Landmark } from 'lucide-react';
import { showSuccess, showError } from '@/lib/swal';

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  bankTransfer: 'Bank Transfer',
  slickpay: 'Open Banking (SlickPay)',
};

export default function ConfirmBankDetailsChangePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState<'confirming' | 'success' | 'error'>('confirming');
  const [message, setMessage] = useState('Confirming your bank details change...');
  const [unitName, setUnitName] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<string>('');

  useEffect(() => {
    const confirmChange = async () => {
      const token = searchParams.get('token');
      const requestId = searchParams.get('requestId');

      if (!token || !requestId) {
        setStatus('error');
        setMessage('Invalid confirmation link. Please check the link from your email and try again.');
        return;
      }

      try {
        const confirmUnitBankDetailsChange = httpsCallable(functions, 'confirmUnitBankDetailsChange');
        const result = await confirmUnitBankDetailsChange({ requestId, token });
        const data = result.data as { success: boolean; unitName: string; paymentMethod: string };

        setStatus('success');
        setUnitName(data.unitName);
        setPaymentMethod(data.paymentMethod);
        setMessage(`Bank details updated for ${data.unitName}.`);
        showSuccess('Bank details change confirmed');
      } catch (error: any) {
        console.error('Error confirming bank details change:', error);
        setStatus('error');
        setMessage(error?.message || 'This confirmation link is invalid or has expired. Please request the change again.');
        showError('Confirmation failed');
      }
    };

    confirmChange();
  }, [searchParams]);

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              {status === 'confirming' && (
                <Loader2 className="h-16 w-16 text-primary animate-spin" />
              )}
              {status === 'success' && (
                <CheckCircle className="h-16 w-16 text-green-600" />
              )}
              {status === 'error' && (
                <XCircle className="h-16 w-16 text-red-600" />
              )}
            </div>
            <CardTitle className="text-2xl">
              {status === 'confirming' && 'Confirming Change'}
              {status === 'success' && 'Bank Details Updated'}
              {status === 'error' && 'Confirmation Failed'}
            </CardTitle>
            <CardDescription>{message}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {status === 'success' && (
              <div className="rounded-lg bg-green-50 border border-green-200 p-4">
                <div className="flex items-start gap-3">
                  <Landmark className="h-5 w-5 text-green-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-900">
                      {PAYMENT_METHOD_LABELS[paymentMethod] || paymentMethod}
                    </p>
                    <p className="text-sm text-green-700 mt-1">
                      The new account details are now active for {unitName}. All Secretaries and Treasurers
                      for this unit have been emailed a summary of what changed.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {status === 'error' && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                <p className="text-sm text-red-900">{message}</p>
              </div>
            )}

            <div className="flex flex-col gap-2">
              {status !== 'confirming' && (
                <Button onClick={() => navigate('/dashboard')} className="w-full">
                  Return to Dashboard
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
